import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import { Outlet } from 'react-router-dom';
import { SingleValue } from 'react-select';
import TabsBar from './Components/TabsBar';
import SearchBar from './Components/SearchBar';
import FiltersPanel from './Components/FiltersPanel';
import { DataTable } from './DataTable';
import PaginationControls from './Components/PaginationControls';
import { useDataViewController } from '../../../hooks/useDataViewController';
import { ConfirmSettings, PageResult, TableOverviewAction } from './TableOverview';
import styles from './DataView.module.scss';
import { SearchFieldsRegistry, SearchModels } from './utils/SearchFieldRegistry';
import { buildSearchDomain } from './utils/buildSearchDomain';
import useFilters, { FilterCondition } from '../../../services/filters';
import { buildFiltersDomain, FilterState, RELATIONAL_FIELDS } from './utils/buildFilterDomain';
import HeadlineWithButtons from '../HeadlineWithButtons/HeadlineWithButtons';
import { KpiRow } from '../../layouts/KpiRow.tsx/KpiRow';
import { KpiModel } from '../../../services/kpi';
import Button, { ButtonToneProps } from '../Button/Button';
import Icon, { IconName } from '../Icon/Icon';
import { OptionItemProps } from '../OptionItem/OptionItem';
import ConfirmationModal from '../../modals/ConfirmationModal/ConfirmationModal';
import { Container } from '../../layouts/Container/Container';
import Select, { Option } from '../Select/Select';
import Checkbox from '../Checkbox/Checkbox';
import ActionMenuButton from '../ActionMenuButton/ActionMenuButton';
import { getPaginationOptions, formatBadgeFilterLabel } from '../../../utils/general';
import Portal from '../../layouts/Portal/Portal';
import Badge from '../Badge/Badge';
import ReferenceFilterBadge from '../ReferenceFilterSelect/ReferenceFilterBadge';
import { isReferenceFilterType } from '../ReferenceFilterSelect/ReferenceFilterSelect';
import SaveSearchModal from '../../modals/SaveSearchModal/SaveSearchModal';
import { getTablePreferences, updateTablePreferences } from '../../../ViewPreferences.storage';

export interface UsePagedQuery<T> {
  (params: {
    page: number;
    size: number;
    sort_by: string | undefined;
    sort_order: 'asc' | 'desc' | undefined;
    domain: string | undefined;
    tabIndex?: number;
    enabled?: boolean;
    onSuccess?: () => void;
  }): {
    data?: PageResult<T>;
    isLoading: boolean;
  };
}
export interface BulkAction<T> {
  title: string;
  icon?: string;
  isDisabled?: (selected: T[]) => boolean;
  bulkSupport?: boolean;
  onExecute: (selected: T[]) => void | Promise<void>;
  confirmSettings?: {
    renderTitle: (selected: T[]) => string;
    renderContent: (selected: T[]) => string;
    confirmLabel: string;
    cancelLabel: string;
  };
}

export interface DataViewAction<T> {
  title: string;
  titlePlural?: string;
  icon?: IconName;
  bulkSupport?: boolean;
  showInContextMenu?: boolean;
  showInToolbar?: boolean;
  toolbarTone?: ButtonToneProps;
  bulkMenuTone?: ButtonToneProps;
  onExecute?: (selected: T[]) => Promise<void> | void;
  isRowClickAction?: boolean;
  isDisabled?: (selected: T[]) => boolean;
  disabledHint?: string;
  confirmSettings?: ConfirmSettings<T>;
  isError?: boolean;
  toast?: {
    renderSuccessMessage: (selected: T[]) => string;
    renderErrorMessage: (selected: T[]) => string;
  };
  menuItems?: OptionItemProps[];
}

export interface DataViewProps<T, TFilters extends Record<string, any> = Record<string, any>> {
  controller: ReturnType<typeof useDataViewController<TFilters>>;
  columns: any[];
  filterModelId: string;
  headline?: string;
  kpiModel?: KpiModel;
  kpiContent?: React.ReactNode;
  useDataQuery: UsePagedQuery<T>;
  modelKey: SearchModels;
  withSaveFilters?: boolean;
  tabs?: { key: string; label: string; value?: number | null }[];
  showTabs?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  showPagination?: boolean;
  applyContainer?: boolean;
  displayActionsColumn?: boolean;
  displayColumnSelect?: boolean;
  renderFilters?: (filters: TFilters, set: (f: TFilters) => void) => React.ReactNode;
  getDomainForTab?: (tabKey: string, tabIndex: number) => string;
  actions: DataViewAction<T>[];
  bulkActions?: BulkAction<T>[];
  allowBulkActions?: boolean;
  bulkIdField?: string;
  fetchAllBulkIds?: (domain: string) => Promise<number[]>;
  initialHiddenColumns?: string[];
  onRowClick?: (row: T) => void;
  noResultsLabel?: string;
  emptyState?: React.ReactNode;
  onDataLoaded?: (items: T[]) => void;
}

function normalizeDomain(input?: string): any[] {
  if (!input) return [];

  const fixed = input.replace(/\(/g, '[').replace(/\)/g, ']');

  try {
    return JSON.parse(fixed);
  } catch (e) {
    console.warn('Domain parse failed, using empty []:', input);
    return [];
  }
}

function extractFilteredFields(filters: FilterState): Set<string> {
  const set = new Set<string>();

  Object.entries(filters).forEach(([field, value]) => {
    if (!value.operator) return;

    const mapped = RELATIONAL_FIELDS[field] ?? field;
    set.add(mapped);
  });

  return set;
}

export function mergeDomain(
  modelKey: SearchModels,
  baseDomain: string | undefined,
  search: string,
  filters: FilterState,
) {
  const parsedBase = normalizeDomain(baseDomain);
  const originalSearchFields = SearchFieldsRegistry[modelKey] ?? [];

  const filteredFields = extractFilteredFields(filters);

  const searchFields = originalSearchFields.filter(f => !filteredFields.has(f));
  const isIntegerField = (field: string) => field === 'id' || field === 'partner_id.zip';

  const searchDomain = buildSearchDomain(search, searchFields, isIntegerField) || [];

  const filtersDomain = buildFiltersDomain(filters) || [];

  const allDomains: any[] = [];
  if (parsedBase.length) allDomains.push(...parsedBase);
  if (filtersDomain.length) allDomains.push(...filtersDomain);
  if (searchDomain.length) allDomains.push(...searchDomain);

  if (allDomains.length === 0) return JSON.stringify([]);
  if (allDomains.length === 1) return JSON.stringify(allDomains);
  const andOperator =
    (parsedBase.length > 0 && filtersDomain.length > 0 && searchDomain.length > 0) || parsedBase.length > 1
      ? ['&', '&']
      : (parsedBase.length > 0 && filtersDomain.length > 0 && searchDomain.length < 1) ||
          (parsedBase.length > 0 && filtersDomain.length < 1 && searchDomain.length > 0) ||
          (parsedBase.length < 1 && filtersDomain.length > 0 && searchDomain.length > 0) ||
          parsedBase.length > 0
        ? ['&']
        : [''];
  return parsedBase.length > 0 || filtersDomain.length > 0
    ? JSON.stringify([...andOperator, ...allDomains])
    : JSON.stringify([...allDomains]);
}

export function DataView<T, TFilters extends Record<string, any> = Record<string, any>>(
  props: DataViewProps<T, TFilters>,
) {
  const {
    filterModelId,
    controller,
    columns,
    headline,
    kpiModel,
    kpiContent,
    useDataQuery,
    modelKey,
    tabs,
    actions,
    withSaveFilters = true,
    applyContainer = true,
    showTabs = true,
    showSearch = true,
    showFilters = true,
    showPagination = true,
    displayActionsColumn = true,
    displayColumnSelect = true,
    getDomainForTab,
    allowBulkActions = true,
    bulkIdField = 'id',
    fetchAllBulkIds,
    initialHiddenColumns,
    noResultsLabel,
    emptyState,
    onDataLoaded,
  } = props;
  const { t } = useTranslation();
  const generalLabels = t('general', { returnObjects: true }) as Record<string, string>;
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [rowSelection, setRowSelection] = useState<any>({});

  const preferences = React.useMemo(() => getTablePreferences(modelKey), [modelKey]);

  const [visibleConfirmDialogIndex, setVisibleConfirmDialogIndex] = useState<number | null>(null);
  const [toConfirmEntities, setToConfirmEntities] = useState<T[] | null>();

  const [saveSearchModal, setSaveSearchModal] = useState(false);
  const [additionalFilterModalOpen, setAdditionalFilterModalOpen] = useState(false);
  const {
    availableFilters = [],
    activeFilters,
    deleteFilterById,
    createFilter,
    setFilter,
    filter,
  } = useFilters(showFilters ? modelKey : '') || {};

  const columnHelper = createColumnHelper<T>();

  const supportsBulk = actions.some(action => action.bulkSupport) && allowBulkActions;
  const visibleConfirmDialogSettings =
    visibleConfirmDialogIndex === null ? null : actions[visibleConfirmDialogIndex].confirmSettings;

  const sort = controller.sorting[0];

  useEffect(() => {
    if (!controller.tab && tabs?.length) {
      controller.setTab(tabs[0].key);
    }
  }, [controller.tab, tabs]);

  const tabDomain =
    controller.tab && getDomainForTab
      ? getDomainForTab(controller.tab, tabs?.findIndex(t => t.key === controller.tab) ?? 0)
      : undefined;

  const previousTabRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!controller.tab) return;

    if (previousTabRef.current === null) {
      previousTabRef.current = controller.tab;
      return;
    }

    if (previousTabRef.current !== controller.tab) {
      controller.setDomain?.(null);
    }

    previousTabRef.current = controller.tab;
  }, [controller.tab]);

  useEffect(() => {
    if (preferences.pageSize && controller.pageSize !== preferences.pageSize) {
      controller.setPageSize(preferences.pageSize);
    }
  }, [preferences.pageSize]);

  useEffect(() => {
    if (preferences.sorting && controller.sorting.length === 0) {
      controller.setSorting(preferences.sorting);
    }
  }, [preferences.sorting]);

  const baseDomain = controller.domain ?? tabDomain;

  const domain = mergeDomain(modelKey, baseDomain, controller.search, controller.filters);

  const queryResult = useDataQuery({
    page: controller.page,
    size: controller.pageSize,
    sort_by: sort?.id,
    // eslint-disable-next-line no-nested-ternary
    sort_order: sort ? (sort.desc ? 'desc' : 'asc') : undefined,
    domain,
  });

  const data: T[] = queryResult.data?.items ?? [];
  const total: number = queryResult.data?.total ?? 0;
  const { isLoading } = queryResult;

  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [offerAcrossPages, setOfferAcrossPages] = useState(false);
  const isWholePageSelected = data.length > 0 && selectedRows.length === data.length;
  const canSelectAllAcrossPages = Boolean(fetchAllBulkIds) && total > data.length;

  const exitSelectAllAcrossPages = () => {
    setSelectAllAcrossPages(false);
    setOfferAcrossPages(false);
    setSelectedRows([]);
    setRowSelection({});
  };

  useEffect(() => {
    setSelectAllAcrossPages(false);
    setOfferAcrossPages(false);
    setSelectedRows([]);
    setRowSelection({});
  }, [domain]);

  useEffect(() => {
    setSelectedRows([]);
    setRowSelection({});
  }, [controller.page]);

  useEffect(() => {
    if (isWholePageSelected && canSelectAllAcrossPages) {
      setOfferAcrossPages(true);
    }
  }, [isWholePageSelected, canSelectAllAcrossPages]);

  const resolveBulkTargets = async (fallback: T[]): Promise<T[]> => {
    if (selectAllAcrossPages && fetchAllBulkIds) {
      const ids = await fetchAllBulkIds(domain);
      return ids.map(id => ({ [bulkIdField]: id }) as unknown as T);
    }
    return fallback;
  };

  useEffect(() => {
    if (onDataLoaded && data.length > 0) {
      onDataLoaded(data);
    }
  }, [data]);

  const executeActionAndShowToast = async (action: TableOverviewAction<T> | undefined, selected: T[]) => {
    try {
      await action?.onExecute?.(selected);
      const successMessage = action?.toast?.renderSuccessMessage?.(selected);
      if (successMessage) {
        toast.success(successMessage);
      }
      if (!successMessage && action?.title.includes('abbrechen')) {
        toast.success(`Aktion: '${action?.title}' war erfolgreich!`);
      }
    } catch (error: any) {
      const errorMessage = action?.toast?.renderErrorMessage?.(selected);
      if (errorMessage) {
        toast.error(errorMessage);
      }
      if (!errorMessage) {
        toast.error(error?.detail);
      }
    }
  };

  const bulkColumn = columnHelper.accessor((row: any) => row.name, {
    id: 'bulk',
    size: 30,
    enableHiding: false,
    header: ({ table }) => (
      <div className="flex items-center gap-x-12">
        <div role="none" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={selectAllAcrossPages || table.getIsAllRowsSelected()}
            indeterminate={!selectAllAcrossPages && table.getIsSomeRowsSelected()}
            onChange={e => {
              if (selectAllAcrossPages) {
                exitSelectAllAcrossPages();
              } else {
                table.getToggleAllRowsSelectedHandler()(e);
                if (!e.target.checked) {
                  setOfferAcrossPages(false);
                }
              }
            }}
          />
        </div>
      </div>
    ),
    cell: info => (
      <div className="flex items-center gap-x-12">
        <div role="none" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={selectAllAcrossPages || info.row.getIsSelected()}
            indeterminate={!selectAllAcrossPages && info.row.getIsSomeSelected()}
            onChange={e => {
              if (selectAllAcrossPages) {
                setSelectAllAcrossPages(false);
                setOfferAcrossPages(false);
                info.table.toggleAllRowsSelected(true);
                info.row.toggleSelected(false);
              } else {
                info.row.getToggleSelectedHandler()(e);
                if (!e.target.checked) {
                  setOfferAcrossPages(false);
                }
              }
            }}
          />
        </div>
      </div>
    ),
  });

  const actionsColumn = columnHelper.display({
    id: 'actions',
    size: 60,
    enableHiding: false,
    enableResizing: false,
    header: () => <Icon className="text-full opacity-60 flex justify-center w-full" name="edit" />,
    cell: ({ row }) => {
      const rowData = row.original;

      return (
        <div className="flex justify-center">
          <ActionMenuButton
            menuItems={actions
              .map((action, actionIndex) => {
                if (!action.showInContextMenu) {
                  return null;
                }
                return {
                  accessoryItem: action.icon,
                  isError: action.isError,
                  label: action.title,
                  disabled: action.isDisabled && action.isDisabled([rowData]),
                  onClick: async (event: any) => {
                    event.stopPropagation();
                    if (action.confirmSettings) {
                      setVisibleConfirmDialogIndex(actionIndex);
                      setToConfirmEntities([rowData]);
                    } else {
                      executeActionAndShowToast(action, [rowData]);
                    }
                  },
                };
              })
              .filter(a => a !== null)}
          />
        </div>
      );
    },
  });

  const finalColumns = (() => {
    const baseColumns = supportsBulk ? [bulkColumn, ...columns] : [...columns];
    return displayActionsColumn ? [...baseColumns, actionsColumn] : baseColumns;
  })();

  const toolbarButtons = actions.map((action, actionIndex) => {
    if (!action.showInToolbar) {
      return null;
    }
    return {
      title: selectedRows.length > 1 && action.titlePlural ? action.titlePlural : action.title,
      tone: action.toolbarTone || ButtonToneProps.PRIMARY_BLUE,
      icon: action.icon,
      menuItems: action.menuItems ?? [],
      onClick: async () => {
        const selected = supportsBulk ? selectedRows : [];
        if (action.confirmSettings) {
          setVisibleConfirmDialogIndex(actionIndex);
          setToConfirmEntities(selected);
        } else {
          try {
            await executeActionAndShowToast(action, selected);
            setSelectedRows([]);
            setRowSelection({});
          } catch (e) {
            console.error(e);
          }
        }
      },
    };
  });

  const addContainer = (
    condition: boolean,
    wrapper: (children: React.ReactNode) => JSX.Element,
    children: React.ReactNode,
  ) => {
    return condition ? wrapper(children) : children;
  };

  const mapToFilterConditions = (filters: Record<string, any>): FilterCondition[] => {
    return Object.entries(filters).map(([field, filter]) => ({
      field,
      operator: filter.operator,
      value: String(filter.value),
      type: filter.type,
    }));
  };

  const mapDomainToFilters = (domain: FilterCondition[]): TFilters => {
    const result: Record<string, any> = {};

    domain.forEach(({ field, operator, value, type }) => {
      result[field] = {
        operator,
        type,
        value,
      };
    });

    return result as TFilters;
  };

  const handleActiveFilterSelect = (e: SingleValue<Option>) => {
    if (e?.label.includes('Neuen Filter erstellen')) {
      setAdditionalFilterModalOpen(true);
    } else {
      if (e && e.value && activeFilters) {
        const selectedFilter = activeFilters.find(filter => filter.id.toString() === e.value);
        if (selectedFilter) {
          controller.setFilters(mapDomainToFilters(selectedFilter.domain));
          setFilter(selectedFilter);
        }
      } else {
        controller.setFilters({} as TFilters);
        setFilter(null);
      }
    }
  };

  const handleCreateFilter = async (name: string) => {
    if (!name) return;

    const filterConditions = mapToFilterConditions(controller.filters);
    await createFilter(filterConditions, name);

    setAdditionalFilterModalOpen(false);
    setSaveSearchModal(false);
  };

  const handleFilterSubmit = () => {
    if (!additionalFilterModalOpen) {
      setSaveSearchModal(true);
    }
    setAdditionalFilterModalOpen(false);
  };

  const handleFilterDelete = () => {
    deleteFilterById(filter?.id ?? 0);
    controller.setFilters({} as TFilters);
  };

  const hasActiveBadges = Object.values(controller.filters ?? {}).some(
    filter => filter.value !== undefined && filter.value !== '',
  );

  const isDefaultTab = !tabs?.length || (controller.tab ?? tabs[0]?.key) === tabs[0]?.key;
  const isUnfiltered = !controller.search && !hasActiveBadges && !controller.domain && isDefaultTab;

  const options = getPaginationOptions();
  return addContainer(
    applyContainer,
    children => <Container>{children}</Container>,
    <div className={styles.root}>
      {(headline || kpiModel || kpiContent) && (
        <div>
          {headline && <HeadlineWithButtons headline={headline} buttons={toolbarButtons.filter(b => b !== null)} />}
          {kpiModel && <KpiRow model={kpiModel} />}
          {kpiContent}
        </div>
      )}
      {showTabs && tabs && (
        <div className="mb-16 pt-36">
          <TabsBar tabs={tabs} value={controller.tab ?? tabs[0]?.key} onChange={controller.setTab} />
        </div>
      )}
      {withSaveFilters && showFilters && (
        <div className="flex items-end">
          {activeFilters && (
            <div className={`${styles.activeFilters}`}>
              <Select
                className="pt-24 mb-16"
                key={`${activeFilters?.length ?? 0}`}
                placeholder={t('general.select_filter')}
                label={t('general.my_filters')}
                isClearable
                defaultValue={activeFilters
                  .map(e => ({ value: e.id.toString(), label: e.name }))
                  .find(e => e.value === filter?.id.toString())}
                options={[
                  { value: '', label: t('general.create_new_filter') },
                  ...(activeFilters?.map(e => ({ value: e.id.toString(), label: e.name })) ?? []),
                ]}
                onChange={handleActiveFilterSelect}
              />
            </div>
          )}
          <div className="ml-auto flex gap-16">
            {!filter && (
              <Button
                className="mb-16"
                onClick={handleFilterSubmit}
                disabled={!Object.values(controller.filters).some(e => !!e.value)}
                tone={ButtonToneProps.PRIMARY_BLUE}
                title={t('general.save_search')}
              />
            )}
            {filter && filter?.id && (
              <Button
                className="mb-16"
                onClick={handleFilterDelete}
                tone={ButtonToneProps.SECONDARY}
                title={t('general.delete_filter')}
              />
            )}
          </div>
        </div>
      )}

      {(showSearch || showFilters) && (
        <div className={`${styles.searchFilterWrapper} ${kpiModel && !showTabs && !showFilters ? 'mt-16' : ''}`}>
          <div className="grid grid-cols-2 gap-x-4 items-end">
            {showSearch && (
              <div className="flex-1 w-full">
                <SearchBar
                  value={controller.search}
                  onChange={controller.setSearch}
                  placeholder={t('general.search_placeholder')}
                />
              </div>
            )}

            {showFilters && (
              <FiltersPanel
                filters={controller.filters}
                onChange={controller.setFilters}
                filterModelId={filterModelId}
                openModalFromOutside={additionalFilterModalOpen}
                setOpenModal={setAdditionalFilterModalOpen}
                primaryKeys={[
                  { key: 'custom', operator: '=' },
                  { key: 'date_planned', operator: '<' },
                ]}
                allAvailableFilters={availableFilters}
                searchOnly={false}
                renderFilters={() => {
                  return null;
                }}
              />
            )}
          </div>
        </div>
      )}

      {hasActiveBadges && (
        <div className="flex gap-8 flex-wrap pt-20">
          {Object.entries(controller.filters!).map(([key, filter]) => {
            if (filter.value === undefined || filter.value === '') return null;

            const fieldLabel = generalLabels[key] ?? formatBadgeFilterLabel(key);
            const value = String(filter.value);
            const removeFilter = () =>
              controller.setFilters(prev => {
                const copy = { ...prev };
                delete copy[key];
                return copy;
              });

            if (isReferenceFilterType(filter.type)) {
              return (
                <ReferenceFilterBadge
                  key={key}
                  type={filter.type as string}
                  value={value}
                  preLabel={`${fieldLabel}:`}
                  onRemove={removeFilter}
                />
              );
            }

            let displayValue = value;
            if (filter.type === 'datetime') {
              displayValue = dayjs(value).format('DD.MM.YYYY');
            } else if (filter.type === 'boolean') {
              displayValue = value.toLowerCase() === 'true' ? t('general.true') : t('general.false');
            } else if (filter.type === 'selection') {
              displayValue = t(`status.${value}`, { defaultValue: formatBadgeFilterLabel(value) });
            }

            return (
              <Badge
                key={key}
                preLabel={`${fieldLabel}:`}
                label={displayValue}
                expanded
                icon="deleteCircle"
                onClick={removeFilter}
              />
            );
          })}
        </div>
      )}

      {saveSearchModal && (
        <SaveSearchModal
          onSubmit={name => handleCreateFilter(name)}
          isOpen={saveSearchModal}
          onClose={() => setSaveSearchModal(false)}
        />
      )}

      <div className={styles.tableWrapper}>
        <DataTable
          columns={finalColumns}
          data={data}
          loading={isLoading}
          initialHiddenColumns={preferences.hiddenColumns ?? initialHiddenColumns}
          displayColumnSelect={displayColumnSelect}
          sorting={controller.sorting}
          onSortingChange={updater => {
            controller.setSorting(prev => {
              const resolvedSorting = typeof updater === 'function' ? updater(prev) : updater;
              updateTablePreferences(modelKey, { sorting: resolvedSorting });
              return resolvedSorting;
            });
          }}
          enableRowSelection
          noResultsLabel={noResultsLabel}
          emptyState={isUnfiltered ? emptyState : undefined}
          onTableRowClick={row => {
            const action = actions.find(action => action.isRowClickAction);
            executeActionAndShowToast(action, [row]);
          }}
          onColumnVisibilityChange={hiddenColumns => {
            updateTablePreferences(modelKey, { hiddenColumns });
          }}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          onSelectedRowsChange={setSelectedRows}
          getRowId={row => JSON.stringify(row)}
        />
      </div>

      {showPagination && (
        <div className={classNames(styles.root, 'w-full flex items-center justify-between pt-16')}>
          <div className="flex items-center gap-x-12">
            <Select
              options={options}
              onChange={opt => {
                const size = +opt.value;
                controller.setPageSize(size);
                updateTablePreferences(modelKey, { pageSize: size });
              }}
              defaultValue={options.find(opt => +opt.value === controller.pageSize)}
              menuPlacement="top"
            />
            <span className="typo-text-12 text-faded whitespace-nowrap">{t('general.per_page')}</span>
          </div>

          <PaginationControls
            page={controller.page}
            pageSize={controller.pageSize}
            total={total}
            onPageChange={controller.setPage}
          />
        </div>
      )}
      {(selectedRows.length > 0 || selectAllAcrossPages || offerAcrossPages) && (
        <Portal>
          <div className={styles.quickActionMenu}>
            <div className={styles.inner}>
              <div className="flex items-center gap-x-12 pr-8">
                {selectAllAcrossPages ? (
                  <>
                    <span className="typo-text-14 text-full whitespace-nowrap">
                      {t('general.all_matching_selected', { count: total })}
                    </span>
                    <Button
                      title={t('general.deselect_all')}
                      tone={ButtonToneProps.SECONDARY}
                      onClick={exitSelectAllAcrossPages}
                    />
                  </>
                ) : offerAcrossPages && selectedRows.length === 0 ? (
                  <>
                    <Button
                      title={t('general.select_all_matching', { count: total })}
                      tone={ButtonToneProps.PRIMARY_BLUE}
                      onClick={() => setSelectAllAcrossPages(true)}
                    />
                    <Button
                      title={t('general.deselect_all')}
                      tone={ButtonToneProps.SECONDARY}
                      onClick={exitSelectAllAcrossPages}
                    />
                  </>
                ) : (
                  <>
                    <span className="typo-text-14 text-full whitespace-nowrap">
                      {t('general.n_selected', { count: selectedRows.length })}
                    </span>
                    {canSelectAllAcrossPages && isWholePageSelected && (
                      <Button
                        title={t('general.select_all_matching', { count: total })}
                        tone={ButtonToneProps.PRIMARY_BLUE}
                        onClick={() => setSelectAllAcrossPages(true)}
                      />
                    )}
                  </>
                )}
              </div>
              {(selectAllAcrossPages || selectedRows.length > 0) &&
                actions.map((action, actionIndex) => {
                  if (!action.bulkSupport) {
                    return null;
                  }
                  const disableSample = selectAllAcrossPages ? data : selectedRows;
                  const actionDisabled = !!action.isDisabled && action.isDisabled(disableSample);
                  return (
                    <Button
                      key={action.title}
                      title={selectedRows.length > 1 && action.titlePlural ? action.titlePlural : action.title}
                      tone={action.bulkMenuTone || ButtonToneProps.SECONDARY}
                      icon={action.icon}
                      disabled={actionDisabled}
                      hoverText={actionDisabled ? action.disabledHint : undefined}
                      onClick={async () => {
                        if (action.confirmSettings) {
                          setVisibleConfirmDialogIndex(actionIndex);
                          setToConfirmEntities(selectedRows);
                        } else {
                          try {
                            const targets = await resolveBulkTargets(selectedRows);
                            await executeActionAndShowToast(action, targets);
                            exitSelectAllAcrossPages();
                          } catch (e) {
                            console.error(e);
                          }
                        }
                      }}
                    />
                  );
                })}
            </div>
          </div>
        </Portal>
      )}

      <ConfirmationModal
        isOpen={Boolean(visibleConfirmDialogSettings)}
        title={visibleConfirmDialogSettings?.renderTitle(toConfirmEntities!) ?? ''}
        description={visibleConfirmDialogSettings?.renderContent(toConfirmEntities!) ?? ''}
        onClose={async confirmed => {
          if (visibleConfirmDialogIndex === null || toConfirmEntities === null) {
            throw new Error(
              `Confirm handling failed because visibleConfirmDialogIndex is ${visibleConfirmDialogIndex} and toConfirmEntities is ${toConfirmEntities}`,
            );
          }
          if (confirmed) {
            const action = actions[visibleConfirmDialogIndex!];
            if (action) {
              try {
                const targets = await resolveBulkTargets(toConfirmEntities!);
                await executeActionAndShowToast(action, targets);
                exitSelectAllAcrossPages();
                setVisibleConfirmDialogIndex(null);
                setToConfirmEntities(null);
              } catch (e) {
                console.error(e);
              }
            }
          } else {
            setVisibleConfirmDialogIndex(null);
            setToConfirmEntities(null);
          }
        }}
      />
      <Outlet />
    </div>,
  );
}
